// Client API for the Fulfillment bucket.
//
// Reuses `manufacturingFetch` from `@/lib/manufacturingClient` (it's
// generic over the path -- adds Bearer + workspace-org headers).
// vercel.json rewrites `/api/fulfillment/*` paths to the
// `/api/admin/action?action=fulfillment-*` dispatcher.

import { manufacturingFetch } from "@/lib/manufacturingClient";
import type { OpsOrderIntakeStatus, OpsOrderIntakeSource, OpsOrderIntakeSalePath } from "@/lib/orderIntakeClient";
import type { MfgUnitOrigin } from "./originEnum";
import type { IntakeFormStatus } from "./intakeFormStatus";

export type FulfillmentUnit = {
  id: string;
  plant_id: string;
  serial: string | null;
  display_label: string;
  origin: MfgUnitOrigin;
  inventory_holder_org_id: string | null;
  notes: string;
  created_at: string;
  updated_at: string;
  intake_form_id: string | null;
  intake_form_status: IntakeFormStatus | null;
};

export type FulfillmentUnitsListResponse = {
  ok: true;
  organization_id: string;
  units: FulfillmentUnit[];
};

export type FulfillmentIntakeForm = {
  id: string;
  organization_id: string;
  mfg_unit_id: string;
  template_id: string | null;
  status: IntakeFormStatus;
  assigned_user_id: string | null;
  due_at: string | null;
  tier_1_responses: Record<string, unknown>;
  tier_2_responses: Record<string, unknown>;
  tier_3_response: string;
  submitted_at: string | null;
  reviewer_user_id: string | null;
  reviewed_at: string | null;
  reviewer_notes: string;
  approved_at: string | null;
  service_request_id: string | null;
  created_at: string;
  updated_at: string;
};

export type FulfillmentIntakeFormsListResponse = {
  ok: true;
  organization_id: string;
  forms: FulfillmentIntakeForm[];
};

export type FulfillmentIntakeTemplate = {
  id: string;
  label: string;
  slug: string;
  tier_1_items: unknown[];
  tier_2_items: unknown[];
  default_due_days: number;
  default_assignee_role: string | null;
  applies_to_origins: string[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

export type FulfillmentIntakeTemplatesListResponse = {
  ok: true;
  organization_id: string;
  templates: FulfillmentIntakeTemplate[];
};

export function listFulfillmentUnits() {
  return manufacturingFetch<FulfillmentUnitsListResponse>("/api/fulfillment/units", {
    method: "GET",
  });
}

export function createFulfillmentEntrySingle(payload: {
  origin: MfgUnitOrigin;
  plant_id?: string;
  notes?: string;
  intake_template_id?: string | null;
  due_days_override?: number;
  assigned_user_id?: string | null;
}) {
  return manufacturingFetch<{
    ok: true;
    unit: FulfillmentUnit;
    serial: string;
    intake_form_id: string | null;
  }>("/api/fulfillment/entry/single", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export type BulkEntryRow = {
  origin: MfgUnitOrigin;
  plant_id?: string;
  notes?: string;
  intake_template_id?: string | null;
};

export function createFulfillmentEntryBulk(rows: BulkEntryRow[]) {
  return manufacturingFetch<{
    ok: true;
    summary: {
      created: number;
      skipped: Array<{ idx: number; error: string }>;
      results: Array<{
        idx: number;
        unit_id: string;
        serial: string;
        intake_form_id: string | null;
      }>;
    };
  }>("/api/fulfillment/entry/bulk", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ rows }),
  });
}

export function listFulfillmentIntakeForms(filters?: {
  status?: IntakeFormStatus;
  scope?: "all" | "mine";
}) {
  const q = new URLSearchParams();
  if (filters?.status) q.set("status", filters.status);
  if (filters?.scope === "mine") q.set("scope", "mine");
  const qs = q.toString();
  const path = qs ? `/api/fulfillment/intake/forms?${qs}` : "/api/fulfillment/intake/forms";
  return manufacturingFetch<FulfillmentIntakeFormsListResponse>(path, { method: "GET" });
}

export function fillFulfillmentIntakeForm(payload: {
  form_id: string;
  tier_1_responses?: Record<string, unknown>;
  tier_2_responses?: Record<string, unknown>;
  tier_3_response?: string;
  submit?: boolean;
}) {
  return manufacturingFetch<{ ok: true; form: FulfillmentIntakeForm }>(
    "/api/fulfillment/intake/form/fill",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
}

export function reviewFulfillmentIntakeForm(payload: {
  form_id: string;
  reviewer_notes?: string;
  tier_2_responses?: Record<string, unknown>;
  send_to_service?: boolean;
}) {
  return manufacturingFetch<{
    ok: true;
    form: FulfillmentIntakeForm;
    next_status: IntakeFormStatus;
    service_request_id?: string;
  }>("/api/fulfillment/intake/form/review", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function markFulfillmentIntakeWorkComplete(payload: { form_id: string }) {
  return manufacturingFetch<{ ok: true; form: FulfillmentIntakeForm }>(
    "/api/fulfillment/intake/form/work-complete",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
}

export function listFulfillmentIntakeTemplates() {
  return manufacturingFetch<FulfillmentIntakeTemplatesListResponse>(
    "/api/fulfillment/intake/templates",
    { method: "GET" }
  );
}

export function upsertFulfillmentIntakeTemplate(payload: {
  id?: string;
  label: string;
  slug: string;
  tier_1_items: unknown[];
  tier_2_items: unknown[];
  default_due_days?: number;
  default_assignee_role?: string | null;
  applies_to_origins?: string[];
  is_active?: boolean;
}) {
  return manufacturingFetch<{ ok: true; template: FulfillmentIntakeTemplate }>(
    "/api/fulfillment/intake/template/upsert",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
}

export function getFulfillmentIntakePendingCount() {
  return manufacturingFetch<{
    ok: true;
    organization_id: string;
    user_id: string;
    count: number;
  }>("/api/fulfillment/intake/pending-count", { method: "GET" });
}

export type FulfillmentOrderRow = {
  id: string;
  status: OpsOrderIntakeStatus;
  source: OpsOrderIntakeSource;
  proposed_sale_path: OpsOrderIntakeSalePath | null;
  display_label: string;
  customer_name: string;
  destination_label: string;
  materialized_deal_id: string | null;
  lead_id: string | null;
  linked_opportunity_id: string | null;
  hold_reason: string | null;
  created_at: string;
  updated_at: string;
};

export type FulfillmentOrdersListResponse = {
  ok: true;
  organization_id: string;
  orders: FulfillmentOrderRow[];
  total: number | null;
};

export function listFulfillmentOrders(params?: {
  status?: OpsOrderIntakeStatus | OpsOrderIntakeStatus[];
  manufacturer_org_id?: string;
  limit?: number;
  offset?: number;
}) {
  const q = new URLSearchParams();
  if (params?.status) {
    q.set(
      "status",
      Array.isArray(params.status) ? params.status.join(",") : params.status,
    );
  }
  if (params?.manufacturer_org_id) q.set("manufacturer_org_id", params.manufacturer_org_id);
  if (typeof params?.limit === "number") q.set("limit", String(params.limit));
  if (typeof params?.offset === "number") q.set("offset", String(params.offset));
  const qs = q.toString();
  const path = qs ? `/api/fulfillment/orders?${qs}` : "/api/fulfillment/orders";
  return manufacturingFetch<FulfillmentOrdersListResponse>(path, { method: "GET" });
}
